'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, Upload } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { Guitar as GuitarType, GuitarStatus, GuitarType as GuitarTypeEnum } from './mockData';

type AddGuitarFormValues = Omit<GuitarType, 'id'>;

interface AddGuitarModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: AddGuitarFormValues) => void;
}

const guitarTypes: GuitarTypeEnum[] = ['Electric', 'Acoustic', 'Bass', 'Classical'];
const statusOptions: GuitarStatus[] = ['active', 'on-loan', 'for-sale', 'archived'];
const statusLabels: Record<GuitarStatus, string> = {
  active: 'Active',
  'on-loan': 'On Loan',
  'for-sale': 'For Sale',
  archived: 'Archived',
};

export default function AddGuitarModal({ open, onClose, onAdd }: AddGuitarModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddGuitarFormValues>({
    defaultValues: {
      status: 'active',
      type: 'Electric',
      purchaseDate: '',
      lastMaintenance: '',
      lastStringChange: '',
      notes: '',
      photoUrl: '',
    },
  });

  function handleClose() {
    reset();
    onClose();
  }

  // Backend integration point: replace with POST /api/collection/guitars
  function onSubmit(data: AddGuitarFormValues) {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      onAdd(data);
      reset();
    }, 800);
  }

  return (
    <Modal open={open} onClose={handleClose} title="Add Guitar to Vault" size="xl">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="px-6 py-5 space-y-6">

          {/* Section: Instrument Identity */}
          <div>
            <h3 className="text-xs font-700 uppercase tracking-widest text-muted-foreground mb-4">Instrument Identity</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Brand */}
              <div>
                <label htmlFor="add-brand" className="block text-sm font-600 text-foreground mb-1.5">
                  Brand <span className="text-negative">*</span>
                </label>
                <input
                  id="add-brand"
                  type="text"
                  placeholder="e.g. Fender, Gibson, PRS"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.brand ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                  {...register('brand', { required: 'Brand is required' })}
                />
                {errors.brand && <p className="text-xs text-negative mt-1.5" role="alert">{errors.brand.message}</p>}
              </div>

              {/* Model */}
              <div>
                <label htmlFor="add-model" className="block text-sm font-600 text-foreground mb-1.5">
                  Model <span className="text-negative">*</span>
                </label>
                <input
                  id="add-model"
                  type="text"
                  placeholder="e.g. American Ultra Stratocaster"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.model ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                  {...register('model', { required: 'Model is required' })}
                />
                {errors.model && <p className="text-xs text-negative mt-1.5" role="alert">{errors.model.message}</p>}
              </div>

              {/* Type */}
              <div>
                <label htmlFor="add-type" className="block text-sm font-600 text-foreground mb-1.5">
                  Guitar Type <span className="text-negative">*</span>
                </label>
                <select
                  id="add-type"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer transition-colors ${errors.type ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                  {...register('type', { required: 'Guitar type is required' })}
                >
                  {guitarTypes.map((t) => (
                    <option key={`type-opt-add-${t}`} value={t}>{t}</option>
                  ))}
                </select>
                {errors.type && <p className="text-xs text-negative mt-1.5" role="alert">{errors.type.message}</p>}
              </div>

              {/* Color */}
              <div>
                <label htmlFor="add-color" className="block text-sm font-600 text-foreground mb-1.5">
                  Color / Finish <span className="text-negative">*</span>
                </label>
                <input
                  id="add-color"
                  type="text"
                  placeholder="e.g. Sunburst, Olympic White"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.color ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                  {...register('color', { required: 'Color is required' })}
                />
                {errors.color && <p className="text-xs text-negative mt-1.5" role="alert">{errors.color.message}</p>}
              </div>

              {/* Serial Number */}
              <div>
                <label htmlFor="add-serial" className="block text-sm font-600 text-foreground mb-1.5">
                  Serial Number <span className="text-negative">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Found on the headstock or neck plate</p>
                <input
                  id="add-serial"
                  type="text"
                  placeholder="e.g. US21-056-7841"
                  className={`w-full h-10 px-3 rounded-lg border text-sm font-mono bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.serialNumber ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                  {...register('serialNumber', { required: 'Serial number is required' })}
                />
                {errors.serialNumber && <p className="text-xs text-negative mt-1.5" role="alert">{errors.serialNumber.message}</p>}
              </div>

              {/* Status */}
              <div>
                <label htmlFor="add-status" className="block text-sm font-600 text-foreground mb-1.5">
                  Collection Status
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Current ownership / availability state</p>
                <select
                  id="add-status"
                  className="w-full h-10 px-3 rounded-lg border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer hover:border-primary/50 transition-colors"
                  {...register('status')}
                >
                  {statusOptions.map((s) => (
                    <option key={`status-opt-add-${s}`} value={s}>{statusLabels[s]}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className="border-border" />

          {/* Section: Purchase Details */}
          <div>
            <h3 className="text-xs font-700 uppercase tracking-widest text-muted-foreground mb-4">Purchase Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Purchase Date */}
              <div>
                <label htmlFor="add-purchase-date" className="block text-sm font-600 text-foreground mb-1.5">
                  Purchase Date <span className="text-negative">*</span>
                </label>
                <input
                  id="add-purchase-date"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.purchaseDate ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                  {...register('purchaseDate', {
                    required: 'Purchase date is required',
                    pattern: {
                      value: /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/,
                      message: 'Use MM/DD/YYYY format',
                    },
                  })}
                />
                {errors.purchaseDate && <p className="text-xs text-negative mt-1.5" role="alert">{errors.purchaseDate.message}</p>}
              </div>

              {/* Purchase Price */}
              <div>
                <label htmlFor="add-price" className="block text-sm font-600 text-foreground mb-1.5">
                  Purchase Price (USD) <span className="text-negative">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-500">$</span>
                  <input
                    id="add-price"
                    type="number"
                    min={0}
                    step={1}
                    placeholder="0"
                    className={`w-full h-10 pl-7 pr-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors tabular-nums ${errors.purchasePrice ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                    {...register('purchasePrice', {
                      required: 'Purchase price is required',
                      valueAsNumber: true,
                      min: { value: 0, message: 'Price must be 0 or greater' },
                    })}
                  />
                </div>
                {errors.purchasePrice && <p className="text-xs text-negative mt-1.5" role="alert">{errors.purchasePrice.message}</p>}
              </div>

              {/* Purchase Location */}
              <div className="sm:col-span-2">
                <label htmlFor="add-location" className="block text-sm font-600 text-foreground mb-1.5">
                  Where Bought <span className="text-negative">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Store name, city, or online platform</p>
                <input
                  id="add-location"
                  type="text"
                  placeholder="e.g. Guitar Center, Los Angeles or Reverb.com"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.purchaseLocation ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                  {...register('purchaseLocation', { required: 'Purchase location is required' })}
                />
                {errors.purchaseLocation && <p className="text-xs text-negative mt-1.5" role="alert">{errors.purchaseLocation.message}</p>}
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className="border-border" />

          {/* Section: Maintenance & Strings */}
          <div>
            <h3 className="text-xs font-700 uppercase tracking-widest text-muted-foreground mb-4">Maintenance & Strings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Last Maintenance */}
              <div>
                <label htmlFor="add-maintenance" className="block text-sm font-600 text-foreground mb-1.5">
                  Last Maintenance Date
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Setup, fret leveling, nut work, etc.</p>
                <input
                  id="add-maintenance"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.lastMaintenance ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                  {...register('lastMaintenance', {
                    pattern: {
                      value: /^$|^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/,
                      message: 'Use MM/DD/YYYY format or leave blank',
                    },
                  })}
                />
                {errors.lastMaintenance && <p className="text-xs text-negative mt-1.5" role="alert">{errors.lastMaintenance.message}</p>}
              </div>

              {/* Last String Change */}
              <div>
                <label htmlFor="add-strings" className="block text-sm font-600 text-foreground mb-1.5">
                  Last String Change Date
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">When were the strings last replaced?</p>
                <input
                  id="add-strings"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.lastStringChange ? 'border-negative' : 'border-input hover:border-primary/50'}`}
                  {...register('lastStringChange', {
                    pattern: {
                      value: /^$|^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/,
                      message: 'Use MM/DD/YYYY format or leave blank',
                    },
                  })}
                />
                {errors.lastStringChange && <p className="text-xs text-negative mt-1.5" role="alert">{errors.lastStringChange.message}</p>}
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className="border-border" />

          {/* Section: Photo & Notes */}
          <div>
            <h3 className="text-xs font-700 uppercase tracking-widest text-muted-foreground mb-4">Photo & Notes</h3>
            <div className="space-y-4">
              {/* Photo URL */}
              <div>
                <label htmlFor="add-photo" className="block text-sm font-600 text-foreground mb-1.5">
                  Photo URL
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Link to an image of the guitar (optional)</p>
                <div className="relative">
                  <Upload size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    id="add-photo"
                    type="url"
                    placeholder="https://…"
                    className="w-full h-10 pl-9 pr-3 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring hover:border-primary/50 transition-colors"
                    {...register('photoUrl')}
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="add-notes" className="block text-sm font-600 text-foreground mb-1.5">
                  Notes / Bijzonderheden
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Mods, condition details, provenance, special features</p>
                <textarea
                  id="add-notes"
                  rows={4}
                  placeholder="e.g. Replaced pickups with Lollar Specials. Minor buckle rash on back. Original case included."
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring hover:border-primary/50 transition-colors resize-none"
                  {...register('notes')}
                />
              </div>
            </div>
          </div>

          {/* Required fields legend */}
          <p className="text-xs text-muted-foreground">
            <span className="text-negative">*</span> Required fields
          </p>
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-card rounded-b-2xl">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-600 text-muted-foreground border border-border rounded-lg hover:bg-muted transition-all duration-150 active:scale-95"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-5 py-2 text-sm font-600 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all duration-150 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ minWidth: '140px', justifyContent: 'center' }}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Adding to Vault…
              </>
            ) : (
              <>Add Guitar to Vault</>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}